/**
 * Downloadable receipt document.
 *
 * The hosted receipt (AutoDispatch `/receipt/payment/<token>`) is the system of record
 * and stays that way: it is signed, it is what the passenger's SMS link opens, and this
 * module never tries to replace or re-sign it. But it is embedded in the sheet inside a
 * `sandbox=""` iframe on another origin, so the app cannot reach into that document to
 * print or save it, and the fleet host sets no `Content-Disposition`.
 *
 * So "Download receipt" is built locally from the same trip/payment values the sheet
 * already renders — the numbers the driver just collected — and written as a standalone
 * HTML file. HTML (not PDF) keeps this dependency-free: it opens in any browser, prints
 * to PDF from there, and needs no rendering library.
 *
 * Line rows are passed in already resolved by the caller so this file owns formatting
 * only, never fare arithmetic.
 */

function escapeHtml(value) {
  return String(value === null || value === undefined ? '' : value).replace(
    /[&<>"']/g,
    (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch])
  );
}

function textValue(value) {
  return String(value === null || value === undefined ? '' : value).trim();
}

/** Mirrors the hosted receipt's date presentation without pulling in a date library. */
export function formatReceiptDate(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return date.toISOString();
  }
}

/**
 * A filesystem-safe name that still identifies the trip: `receipt-trip-1234.html`.
 * Anything unusual in the trip number is stripped rather than escaped, since this
 * value becomes a download filename.
 */
export function receiptFileName(tripNumber) {
  const safe = textValue(tripNumber).replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  return `receipt-trip-${safe || 'unknown'}.html`;
}

/**
 * @param {object} receipt
 * @param {string} [receipt.fleetName]
 * @param {string|number} [receipt.tripNumber]
 * @param {string} [receipt.paidAt]      ISO timestamp of settlement
 * @param {string} [receipt.driver]      driver name / unit, already composed
 * @param {string} [receipt.paymentMethod]
 * @param {Array<{label: string, value: string}>} [receipt.lines]  formatted money strings
 * @param {string} [receipt.total]       formatted money string
 * @param {string} [receipt.hostedUrl]   the signed hosted receipt, linked for reference
 * @returns {string} a complete standalone HTML document
 */
export function buildReceiptHtml(receipt = {}) {
  const fleetName = textValue(receipt.fleetName) || 'Receipt';
  const lines = Array.isArray(receipt.lines) ? receipt.lines : [];
  const rows = lines
    .filter((line) => line && textValue(line.label))
    .map(
      (line) =>
        `      <tr><td>${escapeHtml(line.label)}</td><td>${escapeHtml(line.value)}</td></tr>`
    )
    .join('\n');

  const detail = (label, value) =>
    textValue(value)
      ? `      <tr><td>${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`
      : '';

  // Kept close to the fleet's hosted receipt styling so a downloaded copy and the
  // hosted page read as the same document.
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(fleetName)} payment receipt</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; min-height: 100vh; background: #f4f5f7; color: #252a33; font: 16px/1.45 ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
    main { width: min(100%, 430px); min-height: 100vh; margin: 0 auto; padding: 30px; background: #fff; border: 2px solid #e00000; }
    .brand { display: grid; justify-items: center; gap: 5px; margin: 4px 0 25px; }
    .brand-name { font-family: Arial, sans-serif; font-size: 20px; font-weight: 700; }
    .brand-label { color: #747981; font-family: Arial, sans-serif; font-size: 15px; letter-spacing: .17em; }
    .details, .receipt-lines { width: 100%; border-collapse: collapse; }
    td { padding: 2px 0; vertical-align: top; }
    td:first-child { color: #737983; }
    td:last-child { text-align: right; }
    .rule { margin: 19px 0; border: 0; border-top: 1px dashed #c9bdab; }
    .receipt-lines td { padding: 4px 0; }
    .receipt-lines .total td { padding-top: 17px; border-top: 1px solid #c9bdab; color: #252a33; font-size: 20px; font-weight: 800; }
    .footer { margin: 28px 0 0; color: #878c95; text-align: center; font-size: 14px; word-break: break-all; }
    .footer a { color: inherit; }
    @media print { body { background: #fff; } main { border: 0; } .footer a { text-decoration: none; } }
  </style>
</head>
<body>
  <main>
    <header class="brand">
      <div class="brand-name">${escapeHtml(fleetName)}</div>
      <div class="brand-label">PAYMENT RECEIPT</div>
    </header>
    <table class="details" aria-label="Trip details">
${[
  detail('Trip', receipt.tripNumber ? `#${textValue(receipt.tripNumber)}` : ''),
  detail('Date', formatReceiptDate(receipt.paidAt)),
  detail('Driver', receipt.driver),
]
  .filter(Boolean)
  .join('\n')}
    </table>
    <hr class="rule">
    <table class="receipt-lines" aria-label="Payment total">
${rows}
      <tr class="total"><td>Total paid</td><td>${escapeHtml(textValue(receipt.total))}</td></tr>
    </table>
    <hr class="rule">
    <table class="details" aria-label="Payment method">
${detail('Paid via', receipt.paymentMethod)}
    </table>
    <footer class="footer">
      <div>Thank you for riding with ${escapeHtml(fleetName)}</div>
${
  textValue(receipt.hostedUrl)
    ? `      <div><a href="${escapeHtml(receipt.hostedUrl)}">View this receipt online</a></div>`
    : ''
}
    </footer>
  </main>
</body>
</html>
`;
}

/**
 * Hands the document to the browser as a file download.
 *
 * Uses an object URL + a synthetic click, which is the only save path available to a
 * PWA without a native bridge. The URL is revoked on the next tick — Safari needs the
 * click to have been processed before the blob goes away.
 *
 * @returns {boolean} whether the download was started
 */
export function downloadReceiptHtml(html, fileName) {
  if (typeof document === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) {
    return false;
  }
  let url = null;
  try {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'receipt.html';
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  } catch (e) {
    return false;
  } finally {
    if (url) {
      setTimeout(() => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          /* already revoked */
        }
      }, 0);
    }
  }
}
